// Job-status catalogue editor inside the Settings modal. Admin-only:
// the surrounding modal hides the section for non-admin users.
// Lists every status from production_job_statuses (canonical +
// custom + archived), lets the admin rename / recolour / archive
// each row, and adds new statuses via the inline form. Renames
// cascade to production_jobs.status server-side so existing jobs
// keep their Kanban column + filter chip after a rename.
import { useState } from "react";
import type {
  JobStatusCreateInputT,
  JobStatusDto,
} from "../../../../../../shared/types/production-schedule";
import {
  archiveJobStatus,
  createJobStatus,
  reorderJobStatuses,
  updateJobStatus,
} from "../hooks/useProductionApi";
import { useJobStatusesCatalogue } from "../components/JobStatusesProvider";

export function JobStatusesSection() {
  const { statuses, refetch } = useJobStatusesCatalogue();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);

  // Drag-to-reorder, mirroring StageConfigsSection. Reorder operates
  // on the full catalogue so archived rows keep stable sort_order
  // even when the visible filter is on.
  const onDrop = async (targetKey: string) => {
    setDropTargetKey(null);
    const source = dragKey;
    setDragKey(null);
    if (!source || source === targetKey) return;
    const sourceIdx = statuses.findIndex((s) => s.statusKey === source);
    const targetIdx = statuses.findIndex((s) => s.statusKey === targetKey);
    if (sourceIdx < 0 || targetIdx < 0) return;
    const next = statuses.slice();
    const [moved] = next.splice(sourceIdx, 1);
    if (!moved) return;
    const insertAt = next.findIndex((s) => s.statusKey === targetKey);
    next.splice(insertAt < 0 ? next.length : insertAt, 0, moved);
    setError(null);
    setBusyKey(source);
    try {
      await reorderJobStatuses(next.map((s) => s.statusKey));
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const visible = showArchived ? statuses : statuses.filter((s) => !s.archived);

  const patch = async (statusKey: string, body: Record<string, unknown>) => {
    setBusyKey(statusKey);
    setError(null);
    try {
      await updateJobStatus(statusKey, body);
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const archive = async (statusKey: string) => {
    const target = statuses.find((s) => s.statusKey === statusKey);
    if (!target) return;
    const verb = target.archived ? "restore" : "archive";
    const tail = target.archived
      ? "?"
      : "? Existing jobs keep this label; new jobs won't be able to pick it.";
    if (!window.confirm(`${verb[0]?.toUpperCase()}${verb.slice(1)} "${target.label}"${tail}`)) return;
    setBusyKey(statusKey);
    setError(null);
    try {
      if (target.archived) {
        await updateJobStatus(statusKey, { archived: false });
      } else {
        await archiveJobStatus(statusKey);
      }
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const add = async (input: JobStatusCreateInputT) => {
    setError(null);
    try {
      await createJobStatus(input);
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
        paddingBottom: 8,
        borderBottom: "1px solid var(--bsp-border)",
      }}>
        <span style={{
          fontFamily: "var(--bsp-font-display)",
          fontSize: 11,
          fontWeight: 700,
          color: "var(--bsp-blue)",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
        }}>Job statuses</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>
      <p style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 10, lineHeight: 1.45 }}>
        Rename or recolour the Kanban columns + filter chips. Archive
        hides a status from the picker on new jobs; existing jobs
        carrying the label keep their column until they're moved.
        Renames cascade to every job already on that status.
      </p>
      {error ? (
        <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>
          {error}
        </div>
      ) : null}
      <table className="stages-table">
        <thead>
          <tr>
            <th style={{ width: 24 }} title="Drag to reorder" />
            <th>Label</th>
            <th style={{ width: 80 }}>Colour</th>
            <th style={{ width: 90 }} title="Terminal statuses are excluded from capacity calculations">Terminal</th>
            <th style={{ width: 90 }}>Type</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {visible.map((s) => (
            <StatusRow
              key={s.statusKey}
              row={s}
              busy={busyKey === s.statusKey}
              dragging={dragKey === s.statusKey}
              dropTarget={dropTargetKey === s.statusKey}
              onPatch={(body) => patch(s.statusKey, body)}
              onArchive={() => archive(s.statusKey)}
              onDragStart={() => setDragKey(s.statusKey)}
              onDragOver={() => {
                if (dragKey && dragKey !== s.statusKey && dropTargetKey !== s.statusKey) {
                  setDropTargetKey(s.statusKey);
                }
              }}
              onDragLeave={() => {
                if (dropTargetKey === s.statusKey) setDropTargetKey(null);
              }}
              onDrop={() => void onDrop(s.statusKey)}
              onDragEnd={() => { setDragKey(null); setDropTargetKey(null); }}
            />
          ))}
        </tbody>
      </table>
      <AddStatusForm onAdd={add} />
    </div>
  );
}

function StatusRow({
  row,
  busy,
  dragging,
  dropTarget,
  onPatch,
  onArchive,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: {
  row: JobStatusDto;
  busy: boolean;
  dragging: boolean;
  dropTarget: boolean;
  onPatch: (body: Record<string, unknown>) => void;
  onArchive: () => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const [label, setLabel] = useState(row.label);
  const [color, setColor] = useState(row.color);

  return (
    <tr
      className={row.archived ? "is-skipped" : ""}
      style={{
        opacity: dragging ? 0.4 : 1,
        borderTop: dropTarget ? "2px solid var(--bsp-blue)" : undefined,
        background: dropTarget ? "rgba(27, 68, 156, 0.04)" : undefined,
      }}
      onDragOver={(e) => {
        if (dragging) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver();
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        onDragLeave();
      }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
    >
      <td
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", `status:${row.statusKey}`);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        title="Drag to reorder"
        style={{
          cursor: busy ? "wait" : "grab",
          textAlign: "center",
          color: "var(--bsp-fg-muted)",
          userSelect: "none",
        }}
      >
        ⋮⋮
      </td>
      <td>
        <input
          className="stage-inp"
          style={{ width: "100%" }}
          value={label}
          maxLength={32}
          disabled={busy}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => { if (label !== row.label && label.trim()) onPatch({ label: label.trim() }); }}
        />
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="color"
            value={color}
            disabled={busy}
            onChange={(e) => setColor(e.target.value)}
            onBlur={() => { if (color.toLowerCase() !== row.color.toLowerCase()) onPatch({ color }); }}
            style={{ width: 28, height: 22, border: "1px solid var(--bsp-border)", borderRadius: 3, padding: 0, background: "transparent" }}
          />
          <span style={{ fontFamily: "var(--bsp-font-mono)", fontSize: 10, color: "var(--bsp-fg-muted)" }}>
            {color}
          </span>
        </div>
      </td>
      <td style={{ textAlign: "center" }}>
        <input
          type="checkbox"
          checked={row.isTerminal}
          disabled={busy}
          onChange={(e) => onPatch({ isTerminal: e.target.checked })}
          title="Excluded from bay + assignee capacity rollups"
        />
      </td>
      <td style={{ fontSize: 10, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {row.isCustom ? "Custom" : "Canonical"}
      </td>
      <td style={{ textAlign: "right" }}>
        <button
          type="button"
          className="btn-sm"
          onClick={onArchive}
          disabled={busy}
          style={row.archived ? undefined : { color: "var(--bsp-red-fg)" }}
        >
          {row.archived ? "Restore" : "Archive"}
        </button>
      </td>
    </tr>
  );
}

function AddStatusForm({ onAdd }: { onAdd: (input: JobStatusCreateInputT) => Promise<void> }) {
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#1B449C");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await onAdd({ label: label.trim(), color });
      setLabel("");
      setColor("#1B449C");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 14,
      padding: 12,
      border: "1px dashed var(--bsp-border-strong)",
      borderRadius: "var(--bsp-r-md)",
      background: "var(--bsp-surface-muted)",
    }}>
      <div style={{
        fontFamily: "var(--bsp-font-display)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--bsp-blue)",
        marginBottom: 8,
      }}>Add custom status</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 220px", fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Label
          <input
            className="stage-inp"
            placeholder="e.g. On Hold"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={32}
            disabled={busy}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Colour
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={busy}
            style={{ width: 40, height: 28, border: "1px solid var(--bsp-border)", borderRadius: 3, padding: 0, background: "transparent" }}
          />
        </label>
        <button
          type="button"
          className="btn-submit"
          onClick={() => void submit()}
          disabled={busy || !label.trim()}
        >
          {busy ? "Adding…" : "Add status"}
        </button>
      </div>
    </div>
  );
}
